import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Image, Spin } from 'antd';
import Title from 'antd/lib/typography/Title';
import BookyBox from '../../Components/Layout/BookyBox';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import { PublicSiteMenu } from '../../Components/PublicSite/PublicSiteMenu';
import NotFound from '../../Components/Page/NotFoundPage';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { WebApiCompanyDtoCompanyPublicSiteDto } from '../../Repository/eventbookyapi';
import PublicSiteFamilyMembers from './PublicSiteFamilyMembers';

enum State {
    Loading,
    CompanyNotFound,
    Ready
}

/**
 * Own route/tab (`/familymembers`) for a primary member to manage their household's family
 * members from the public site - split out of PublicSiteEditMember so it shows up as its own
 * nav item in PublicSiteMenu, mirroring PublicSiteMemberSearch's page shape. Only reachable when
 * the company has FamilyMemberSelfServiceLimit > 0 - redirects back to the profile page otherwise
 * (e.g. someone navigating here directly after an admin turned the feature off).
 */
const PublicSiteFamilyMembersPage = () => {
    const params = useParams();
    const navigation = useNavigate();
    const GetText = TextService.GetText;

    const [state, setState] = useState(State.Loading);
    const [publicSite, setPublicSite] = useState({} as WebApiCompanyDtoCompanyPublicSiteDto);
    const [limit, setLimit] = useState(0);

    useEffect(() => {
        const loadData = async () => {
            if (!params.companyurl) {
                setState(State.CompanyNotFound);
                return;
            }

            try {
                const company = await PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl);
                setPublicSite(company);

                const session = await PublicSiteService.GetSession();
                const sessionLimit = session.familyMemberSelfServiceLimit ?? 0;

                if (sessionLimit <= 0) {
                    navigation('/' + getCurrentLanguage() + '/' + params.companyurl + '/editmember', { replace: true });
                    return;
                }

                setLimit(sessionLimit);
                setState(State.Ready);
            } catch {
                setState(State.CompanyNotFound);
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.companyurl]);

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    if (state === State.CompanyNotFound) {
        return <NotFound message={GetText('PublicSiteEditMember_CompanyNotFound')} />;
    }

    if (state === State.Loading) {
        return (
            <PagePublicSite>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </PagePublicSite>
        );
    }

    return (
        <PagePublicSite>
            {publicSite.siteLogoPath ? (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                </div>
            ) : (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                </div>
            )}
            <PublicSiteMenu />

            <BookyBox>
                <PublicSiteFamilyMembers limit={limit} />
            </BookyBox>
        </PagePublicSite>
    );
};

export default PublicSiteFamilyMembersPage;
